/**
 * mSTEM EXT-01 device declaration modal container (Phase 6 / Plan 06-02,
 * MOCK-ONLY / DR-01). Drives the 4-step wizard state machine:
 *   step 0 — name (required + unique) + type dropdown + icon (FileReader)
 *   step 1 — protocol radio group + optional params
 *   step 2 — peripheral multi-select
 *   step 3 — read-only review → Lưu
 *
 * On Lưu: persist to mstem.customDevices via addCustomDevice, refresh the
 * device library (dispatch setDeviceData(makeDeviceLibrary())) so the created
 * device surfaces as a card, fire the extDeviceSaved success toast, reset, close.
 *
 * Icon "upload" reads the chosen file client-side via FileReader.readAsDataURL
 * (never uploaded). Oversized icons (>100KB, iconWithinCap) are rejected and
 * fall back to the default icon + the errIcon hint (threat T-06-02-01).
 *
 * React 16 class component (no hooks); handlers bound via lodash.bindall.
 */
import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';

import {closeDeviceDeclaration, setExtDeviceEditEntry} from '../reducers/modals';
import {setDeviceData} from '../reducers/device-data';
import {showAlertWithTimeout} from '../reducers/alerts';
import {
    addCustomDevice,
    readCustomDevices,
    iconWithinCap
} from '../lib/mstem-extension-storage.js';
import {makeDeviceLibrary} from '../lib/libraries/devices/index.jsx';
import {generateId} from '../lib/mstem-share-link.js';
import MOCK_PERIPHERALS from '../lib/mstem-mock/ext-peripherals.js';

import DeviceDeclarationModalComponent from '../components/device-declaration-modal/device-declaration-modal.jsx';

// Default device icon when no (or an oversized) icon is provided. Inline SVG
// data-URL so the mock card always renders something (T-06-02-01 fallback).
const DEFAULT_DEVICE_ICON =
    // eslint-disable-next-line max-len
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCI+PHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iMTIiIGZpbGw9IiNFRUYxRkEiLz48cmVjdCB4PSIxOCIgeT0iMjAiIHdpZHRoPSIyOCIgaGVpZ2h0PSIyNCIgcng9IjQiIGZpbGw9IiMxNjJBNzUiLz48L3N2Zz4=';

const emptyState = () => ({
    step: 0,
    deviceName: '',
    deviceType: '',
    iconDataUrl: null,
    iconError: false,
    protocol: null,
    protocolParams: '',
    peripherals: [],
    nameError: null
});

// UC-43 S-2 edit mode — seed the wizard from an existing mstem.customDevices
// entry so "Sửa" opens prefilled. editId is retained so handleSave updates the
// same record (upsert-by-id) instead of creating a duplicate.
const stateFromEntry = entry => ({
    step: 0,
    deviceName: entry.name || '',
    deviceType: entry.type || '',
    iconDataUrl: entry.iconURL || null,
    iconError: false,
    protocol: entry.protocol || null,
    protocolParams: entry.protocolParams || '',
    peripherals: Array.isArray(entry.peripherals) ? entry.peripherals : [],
    nameError: null
});

class DeviceDeclarationModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleName',
            'handleType',
            'handleIcon',
            'applyIconDataUrl',
            'handleProtocol',
            'handleProtocolParams',
            'handlePeripheralToggle',
            'handleNext',
            'handleBack',
            'handleSave',
            'handleRequestClose'
        ]);
        // editId !== null → UC-43 S-2 edit mode (update-by-id on save).
        this.editId = (props.editEntry && props.editEntry.id) || null;
        this.state = props.editEntry ? stateFromEntry(props.editEntry) : emptyState();
    }
    computeNameError (name) {
        const trimmed = (name || '').trim();
        if (!trimmed) return 'name';
        // In edit mode the entry's own current name must not count as a duplicate.
        const existing = readCustomDevices()
            .filter(d => d.id !== this.editId)
            .map(d => (d.name || '').trim().toLowerCase());
        if (existing.indexOf(trimmed.toLowerCase()) !== -1) return 'dup';
        return null;
    }
    handleName (value) {
        // The presentational component passes the raw string (onName(e.target.value)),
        // exactly like onType/onProtocol — not the DOM event. Reading e.target.value
        // here threw "Cannot read properties of undefined" on every keystroke, which
        // made the controlled input revert to empty (device name field unusable).
        this.setState({deviceName: value, nameError: this.computeNameError(value)});
    }
    handleType (value) {
        this.setState({deviceType: value});
    }
    handleIcon (file) {
        if (!file) {
            this.setState({iconDataUrl: null, iconError: false});
            return;
        }
        const reader = new FileReader();
        reader.onload = () => this.applyIconDataUrl(reader.result);
        reader.readAsDataURL(file);
    }
    applyIconDataUrl (dataUrl) {
        if (iconWithinCap(dataUrl)) {
            this.setState({iconDataUrl: dataUrl, iconError: false});
        } else {
            // oversized → drop the user icon, fall back to default + hint
            this.setState({iconDataUrl: null, iconError: true});
        }
    }
    handleProtocol (value) {
        this.setState({protocol: value});
    }
    handleProtocolParams (value) {
        this.setState({protocolParams: value});
    }
    handlePeripheralToggle (id) {
        this.setState(prev => {
            const has = prev.peripherals.indexOf(id) !== -1;
            return {
                peripherals: has ?
                    prev.peripherals.filter(p => p !== id) :
                    prev.peripherals.concat(id)
            };
        });
    }
    handleNext () {
        const {step, deviceName, deviceType, protocol} = this.state;
        if (step === 0) {
            const nameError = this.computeNameError(deviceName);
            if (nameError || !deviceType) {
                this.setState({nameError});
                return;
            }
        }
        if (step === 1 && !protocol) return;
        if (step < 3) this.setState({step: step + 1});
    }
    handleBack () {
        if (this.state.step > 0) this.setState({step: this.state.step - 1});
    }
    handleSave () {
        const {deviceName, deviceType, protocol, protocolParams, peripherals, iconDataUrl} = this.state;
        const isEdit = Boolean(this.editId);
        const device = {
            // Reuse the existing id when editing → addCustomDevice upserts by id
            // (UC-43 S-2: update the same record, never duplicate).
            id: this.editId || `custom-${generateId()}`,
            name: deviceName,
            type: deviceType,
            protocol: protocol,
            protocolParams: protocolParams,
            peripherals: peripherals,
            iconURL: iconDataUrl || DEFAULT_DEVICE_ICON
        };
        addCustomDevice(device);
        // refresh the device library so the new/updated device surfaces as a card
        this.props.onSetDeviceData(makeDeviceLibrary());
        showAlertWithTimeout(this.props.dispatch, isEdit ? 'extDeviceUpdated' : 'extDeviceSaved');
        this.resetAndClose();
    }
    resetAndClose () {
        this.editId = null;
        this.props.onClearEditEntry();
        this.setState(emptyState());
        this.props.onClose();
    }
    handleRequestClose () {
        this.resetAndClose();
    }
    render () {
        return (
            <DeviceDeclarationModalComponent
                deviceName={this.state.deviceName}
                deviceType={this.state.deviceType}
                iconDataUrl={this.state.iconDataUrl}
                iconError={this.state.iconError}
                nameError={this.state.nameError}
                peripherals={this.state.peripherals}
                peripheralsList={MOCK_PERIPHERALS}
                protocol={this.state.protocol}
                protocolParams={this.state.protocolParams}
                step={this.state.step}
                onBack={this.handleBack}
                onIcon={this.handleIcon}
                onName={this.handleName}
                onNext={this.handleNext}
                onPeripheralToggle={this.handlePeripheralToggle}
                onProtocol={this.handleProtocol}
                onProtocolParams={this.handleProtocolParams}
                onRequestClose={this.handleRequestClose}
                onSave={this.handleSave}
                onType={this.handleType}
            />
        );
    }
}

DeviceDeclarationModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    editEntry: PropTypes.shape({id: PropTypes.string}), // null = create mode
    onClearEditEntry: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSetDeviceData: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    editEntry: state.scratchGui.modals.extDeviceEditEntry
});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onClose: () => dispatch(closeDeviceDeclaration()),
    onClearEditEntry: () => dispatch(setExtDeviceEditEntry(null)),
    onSetDeviceData: data => dispatch(setDeviceData(data))
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(DeviceDeclarationModal);
